/**
 * Button Component
 * 
 * A reusable button component with multiple variants, sizes, and styling options.
 * Provides consistent button styling across the application with support for
 * different visual states and accessibility features.
 */

import React from 'react';

/**
 * Props interface for Button component
 * 
 * Extends native button element props with additional styling options
 */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'; // Visual style variant
  size?: 'sm' | 'md' | 'lg';                               // Size variant
  children: React.ReactNode;                               // Button content
}

/**
 * Button Component
 * 
 * Renders a styled button with customizable appearance and behavior.
 * Supports multiple variants (primary, secondary, outline, ghost) and
 * sizes (sm, md, lg) with proper focus states and accessibility.
 * 
 * @param variant - Visual style of the button (default: 'primary')
 * @param size - Size of the button (default: 'md')
 * @param children - Content to display inside the button
 * @param className - Additional CSS classes
 * @param props - Additional HTML button attributes
 * @returns JSX element containing the styled button
 */
const Button: React.FC<ButtonProps> = ({ 
  variant = 'primary', 
  size = 'md', 
  children, 
  className = '', 
  ...props 
}) => {
  // Base classes applied to all button variants - includes responsive improvements
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 touch-target min-h-[44px] min-w-[44px]';
  
  // Variant-specific styling classes
  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white focus:ring-primary-500',         // Primary action button
    secondary: 'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-gray-800 focus:ring-gray-500',           // Secondary action button
    outline: 'border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 text-gray-700 focus:ring-primary-500', // Outlined button
    ghost: 'text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus:ring-gray-500',                           // Minimal styling button
  };
  
  // Size-specific classes for padding and text size - responsive
  const sizeClasses = {
    sm: 'px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm',  // Small button
    md: 'px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base',    // Medium button (default)
    lg: 'px-4 py-2 sm:px-6 sm:py-3 text-base sm:text-lg',  // Large button
  };
  
  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
